import { Button } from '@react-spectrum/s2';
import { useCallback, useRef, useState, type ReactNode } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { CommentWatcher } from '@/components/artifact/CommentWatcher';
import { resolveCommentWatcherMode } from '@/components/artifact/commentWatcherUtils';
import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import {
	MonitorConfigBody,
	type MonitorBodyHandlers,
} from '@/spaces/chat/panels/MonitorConfigBody';
import {
	hasRejectedStoredTarget,
	isMonitorShapeComplete,
} from '@/spaces/chat/panels/monitorSummary';
import { resolveProcessTitle } from '@/spaces/chat/panels/panelTitle';
import { QuietSuccessInterstitial } from '@/spaces/chat/panels/QuietSuccessInterstitial';
import { SubredditMonitorEditor } from '@/spaces/chat/panels/SubredditMonitorEditor';
import { useProcessActiveSwitch } from '@/spaces/chat/panels/useProcessActiveSwitch';

import type {
	IGCommentWatcherClassContent,
	Artifact,
	CustomMonitor,
	SubredditMonitor,
} from '../../../../../bridge/types/artifact.ts';
import type { PanelProcess } from '../../../../../bridge/types/state.ts';
import {
	getMonitorArtifacts,
	type MonitorPanelType,
} from '../../../../../bridge/utils/processClassification.ts';

interface MonitorPanelConfig {
	titleFallback: string;
	// 'auto': the editor self-persists each change via do_edit_artifact (no Save).
	// 'manual': the panel owns unsaved-edit state, a Save button, and the
	// success interstitial; persistence happens on Save via do_save_artifact.
	saveFlow: 'auto' | 'manual';
	// Content the backend would reject must not be saveable. The PATCH route
	// does not re-validate and the post-update hook swallows the error, so an
	// invalid save persists silently and stops recomputing user_summary — with
	// no failure banner, because the request itself succeeded.
	isSaveable?: (artifact: Artifact) => boolean;
	// A monitor whose body already states what it does, derived from its live
	// fields, suppresses the seeded description under the title — otherwise the
	// panel says the same thing twice and the authored half goes stale on the
	// first edit. Same reasoning as SeededJobPanel's showDescription.
	showDescription?: boolean;
	renderBody: (
		artifact: Artifact,
		handlers: MonitorBodyHandlers,
		context: MonitorPanelBodyContext,
	) => ReactNode;
}

// Extra, panel-owned state a body may need beyond its edit handlers. Kept
// separate from MonitorBodyHandlers because it isn't a callback the body
// invokes — it's state the panel already tracks that the body needs to read.
interface MonitorPanelBodyContext {
	// True when the panel knows the last save attempt did not reach the
	// document. MonitorConfigBody's echo-baseline records otherwise assume
	// their own write landed, so a retry of the same edit after a real
	// failure would silently send nothing (ADO-2047).
	saveFailed: boolean;
}

// Single source of truth for which monitor types open an editor here. The keys
// are the MonitorPanelType union (kept in sync with MONITOR_PANEL_TYPES in
// processClassification.ts), so the classifier and this registry cannot drift —
// classifyProcessPanel only returns "monitor" for a child type that has an entry.
const MONITOR_PANEL_CONFIG: Record<MonitorPanelType, MonitorPanelConfig> = {
	subreddit_monitor: {
		titleFallback: 'Monitor',
		saveFlow: 'auto',
		renderBody: (artifact) => (
			<div className="pt-4">
				<SubredditMonitorEditor artifact={artifact as SubredditMonitor} />
			</div>
		),
	},
	ig_comment_watcher_class: {
		titleFallback: 'Comment Monitor',
		saveFlow: 'manual',
		renderBody: (artifact, { onContentChange }) => (
			<CommentWatcher
				content={artifact.content as IGCommentWatcherClassContent}
				mode={resolveCommentWatcherMode(
					(artifact.content as { type?: unknown }).type,
				)}
				isReadOnly={false}
				onContentChange={onContentChange}
			/>
		),
	},
	custom_monitor: {
		titleFallback: 'Monitor',
		saveFlow: 'manual',
		showDescription: false,
		// Same rule the draft panel gates "Start monitoring" on.
		isSaveable: (artifact) =>
			isMonitorShapeComplete((artifact as CustomMonitor).content),
		renderBody: (artifact, handlers, { saveFailed }) => (
			<MonitorConfigBody
				artifact={artifact as CustomMonitor}
				handlers={handlers}
				showMetaRow
				saveFailed={saveFailed}
			/>
		),
	},
};

interface MonitorProcessPanelProps {
	panelProcess: PanelProcess;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
}

export const MonitorProcessPanel = ({
	panelProcess,
	onBack,
	onClose,
	style,
}: MonitorProcessPanelProps) => {
	const bridgeApi = useBridgeApi();
	const [hasUnsavedEdits, setHasUnsavedEdits] = useState(false);
	const [justSaved, setJustSaved] = useState(false);

	const { headerTagRender, actionFailureRender, pausedCaveatRender } =
		useProcessActiveSwitch({
			processId: panelProcess.id,
			status: panelProcess.status,
			updatedAt: panelProcess.updated_at,
			lastActionFailed: panelProcess.last_action_failed,
			pauseAriaLabel: 'Pause monitor',
			resumeAriaLabel: 'Resume monitor',
		});

	// A body (e.g. MonitorConfigBody) can buffer edits and hand us a flush
	// function; we run it right before do_save_artifact so buffered edits land in
	// the same save.
	const flushRef = useRef<(() => void) | null>(null);
	const registerFlush = useCallback((flush: () => void) => {
		flushRef.current = flush;
	}, []);

	// The save-failure banner reads the most recent save result the bridge wrote
	// onto the open panel.
	const saveResult =
		bridgeApi.user_state?.chat_panel?.panel_type === 'process'
			? bridgeApi.user_state.chat_panel.save_result
			: null;
	const saveFailed = saveResult?.ok === false;

	const monitorChild = getMonitorArtifacts(panelProcess)[0];
	// Seeded from the STORED targets so the gate is closed on a blank or a
	// page before MonitorConfigBody has reported. Seeded false, Save would
	// stand open on first render.
	const [hasRejectedTarget, setHasRejectedTarget] = useState(() =>
		monitorChild
			? hasRejectedStoredTarget((monitorChild as CustomMonitor).content)
			: false,
	);
	const config = monitorChild
		? MONITOR_PANEL_CONFIG[monitorChild.type as MonitorPanelType]
		: undefined;

	const handleContentChange = useCallback(
		(updates: Record<string, unknown>) => {
			if (!monitorChild) return;
			// Sync live panel state; the do_edit_artifact reducer routes these
			// edits into `content`. For the manual save flow, persistence happens
			// on Save via do_save_artifact.
			bridgeApi.sendAction({
				type: 'do_edit_artifact',
				artifact_id: monitorChild.id,
				edits: updates,
			});
			setHasUnsavedEdits(true);
			setJustSaved(false);
		},
		[bridgeApi, monitorChild],
	);

	// A buffered body (cadence controls) reports a pending change without
	// persisting — this enables Save without a live do_edit_artifact.
	const handleDirty = useCallback(() => {
		setHasUnsavedEdits(true);
		setJustSaved(false);
	}, []);

	const handleSave = useCallback(() => {
		if (!monitorChild) return;
		// Flush any buffered edits into panel state first, so do_save_artifact
		// persists them together.
		flushRef.current?.();
		bridgeApi.sendAction({
			type: 'do_save_artifact',
			artifact_id: monitorChild.id,
		});
		setHasUnsavedEdits(false);
		setJustSaved(true);
	}, [bridgeApi, monitorChild]);

	const title = resolveProcessTitle(
		panelProcess,
		config?.titleFallback ?? 'Monitor',
	);
	// A monitor's description is authored once, at creation, and restates the very
	// fields shown below it — sources, cadence, destination — all of which the owner
	// can edit here. So it is wrong the moment they change anything, and there is no
	// way to keep it true. The live summary at the top of the body says the same
	// thing derived from the current values instead.
	const description =
		config?.showDescription === false
			? undefined
			: panelProcess.description?.trim() || undefined;

	if (!monitorChild || !config) {
		return (
			<ChatPanelLayout
				title={title}
				description={description}
				headerTagRender={headerTagRender}
				onBack={onBack}
				onClose={onClose}
				style={style}
			>
				<div className="flex flex-1 items-center justify-center p-4">
					<p className="text-(--text-primary)">No monitor configured.</p>
				</div>
			</ChatPanelLayout>
		);
	}

	const isManualSave = config.saveFlow === 'manual';
	// The success interstitial only shows on a clean save; a failed save keeps
	// the editor (plus the Save button) so the user can retry.
	const showSuccessInterstitial = justSaved && !saveFailed;
	// A rejected target is buffered inside the body and never written to the
	// artifact, so `isSaveable` — which reads the stored content — cannot see it.
	const isSaveable =
		!hasRejectedTarget && (config.isSaveable?.(monitorChild) ?? true);

	const actionRenderer =
		isManualSave && !showSuccessInterstitial
			? () => (
					<Button
						type="button"
						variant="accent"
						onPress={handleSave}
						isDisabled={!isSaveable || (!hasUnsavedEdits && !saveFailed)}
					>
						Save
					</Button>
				)
			: undefined;

	return (
		<ChatPanelLayout
			title={showSuccessInterstitial ? 'Saved' : title}
			description={showSuccessInterstitial ? undefined : description}
			headerTagRender={showSuccessInterstitial ? undefined : headerTagRender}
			actionRenderer={actionRenderer}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{justSaved && !saveFailed ? (
				<QuietSuccessInterstitial
					title="Changes saved"
					summary="Your monitor has been updated."
					secondaryAction={{ label: 'Close', onPress: onClose }}
					primaryAction={{
						label: 'Keep editing',
						onPress: () => setJustSaved(false),
					}}
				/>
			) : (
				<>
					{actionFailureRender()}
					{pausedCaveatRender()}
					{saveFailed && (
						<div
							role="alert"
							className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3
								py-2 text-sm font-medium text-red-700"
						>
							Couldn’t save your changes — try again.
						</div>
					)}
					{config.renderBody(
						monitorChild,
						{
							onContentChange: handleContentChange,
							onDirty: handleDirty,
							registerFlush,
							onRejectedTargetChange: setHasRejectedTarget,
						},
						{ saveFailed },
					)}
				</>
			)}
		</ChatPanelLayout>
	);
};
